import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";
import {
  recordingInstructionsSchema,
  skipShortcutsSchema,
} from "./recording-instructions";
import { transcriptionRetrySchema } from "./transcription-retry";

const languageAliases: Record<string, string[]> = {
  eng: ["en", "english"],
  spa: ["es", "spanish"],
  fra: ["fr", "french"],
  jpn: ["ja", "japanese"],
  por: ["pt", "portuguese"],
  hin: ["hi", "hindi"],
  zho: ["zh", "chinese"],
  ukr: ["uk", "ukrainian"],
  rus: ["ru", "russian"],
  ita: ["it", "italian"],
  vie: ["vi", "vietnamese"],
  bel: ["be", "belarusian"],
  ben: ["bn", "bengali"],
  bos: ["bs", "bosnian"],
  bul: ["bg", "bulgarian"],
  cat: ["ca", "catalan"],
  ces: ["cs", "czech"],
  dan: ["da", "danish"],
  nld: ["nl", "dutch"],
  est: ["et", "estonian"],
  fin: ["fi", "finnish"],
  glg: ["gl", "galician"],
  deu: ["de", "german"],
  ell: ["el", "greek"],
  guj: ["gu", "gujarati"],
  hun: ["hu", "hungarian"],
  ind: ["id", "indonesian"],
  kan: ["kn", "kannada"],
  kaz: ["kk", "kazakh"],
  lav: ["lv", "latvian"],
  lit: ["lt", "lithuanian"],
  mkd: ["mk", "macedonian"],
  msa: ["ms", "malay"],
  mal: ["ml", "malayalam"],
  mar: ["mr", "marathi"],
  nep: ["ne", "nepali"],
  nob: ["no", "norwegian"],
  ori: ["or", "odia"],
  fas: ["fa", "persian"],
  pol: ["pl", "polish"],
  ron: ["ro", "romanian"],
  srp: ["sr", "serbian"],
  slk: ["sk", "slovak"],
  slv: ["sl", "slovenian"],
  swe: ["sv", "swedish"],
  tam: ["ta", "tamil"],
  tel: ["te", "telugu"],
  tur: ["tr", "turkish"],
};
// The recorder language pickers (web, Chrome extension, realtime transcriber)
// use their own codes for two languages and offer "Detect automatically".
// Those are the input language of the recording, so accept them here.
// Older recorder pickers (web, extension, offline queue) still send these
// codes; map them to ISO 639-3 so stored and queued recordings are accepted.
const recorderLanguageAliases: Record<string, string> = {
  cmn: "zho",
  jap: "jpn",
};
/**
 * ISO 639-3 "undetermined": the recording language was left on "Detect
 * automatically". Three letters, like every stored language code. Batch
 * transcription after Stop auto-detects the language for these recordings.
 */
export const UNDETERMINED_LANGUAGE = "und";
export const visitLanguageSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((value) => {
    if (value === "auto") return UNDETERMINED_LANGUAGE;
    const recorder = recorderLanguageAliases[value];
    if (recorder) return recorder;
    return (
      Object.entries(languageAliases).find(
        ([code, aliases]) => code === value || aliases.includes(value),
      )?.[0] ?? value
    );
  })
  .pipe(z.enum([...Object.keys(languageAliases), UNDETERMINED_LANGUAGE]));

const visitFields = {
  name: z.string().trim().min(1).max(255),
  title: z
    .string()
    .trim()
    .refine(
      (value) => Array.from(value).length <= 128,
      "Title exceeds 128 characters",
    )
    .transform((value) => value || undefined)
    .optional(),
  duration: z.number().finite().min(0).max(2147483647).transform(Math.ceil),
  style: z.string().trim().min(1).max(64),
  format: z.string().trim().min(1).max(64),
  exam: z.string().trim().max(64),
  template: z.string().trim().min(1).max(64),
  language: visitLanguageSchema,
  customTemplatePublicId: publicIdSchema.optional(),
  // Typed during the recording; see recording-instructions.ts. Present (even
  // empty) when the client wants the server to apply them and the spoken
  // "insert <shortcut>" cue after the note is written.
  instructions: recordingInstructionsSchema.optional(),
  // With `instructions`: shortcut cues dismissed during the recording.
  skipShortcuts: skipShortcutsSchema.optional(),
  // The clinician ticked "Patient consented to recording" in the recorder
  // (RECORDING_CONSENT_ENABLED on the API). Sent only when ticked; the note
  // then starts with a fixed consent line added by the server.
  recordingConsent: z.literal(true).optional(),
};
const consistentTemplate = (value: {
  template: string;
  customTemplatePublicId?: string;
}): boolean =>
  value.template === "Custom"
    ? value.customTemplatePublicId !== undefined
    : value.customTemplatePublicId === undefined;
// Custom templates and Voice to Text Dictation have no style/format options,
// so the web form sends them empty. Transcript (realtime) and audio visits
// share this rule; every other template still needs both.
const templateWithoutOptions = (template: string): boolean =>
  template === "Custom" || template === "Voice to Text Dictation";
const templateOptions = (value: {
  template: string;
  style: string;
  format: string;
}): boolean =>
  templateWithoutOptions(value.template) ||
  (value.style.length > 0 && value.format.length > 0);
const templateOptionsMessage =
  "Style and format are required for this template";
export const visitTranscriptCreateSchema = z
  .strictObject({
    ...visitFields,
    style: z.string().trim().max(64),
    format: z.string().trim().max(64),
    transcription: z
      .string()
      .refine((value) => value.trim().length > 0, "Transcript is required")
      .refine(
        (value) => new TextEncoder().encode(value).length <= 60000,
        "Transcript exceeds 60000 UTF-8 bytes",
      ),
  })
  .refine(consistentTemplate, "Custom template selection is inconsistent")
  .refine(templateOptions, templateOptionsMessage);
export const visitAudioSettingsSchema = z
  .strictObject({
    ...visitFields,
    style: z.string().trim().max(64),
    format: z.string().trim().max(64),
    name: z
      .string()
      .trim()
      .min(1)
      .refine(
        (value) => Array.from(value).length <= 128,
        "Display name exceeds 128 characters",
      ),
    duration: visitFields.duration.refine(
      (value) => value > 0,
      "Audio duration must be positive",
    ),
    diarize: z.boolean().default(false),
    /** @deprecated Ignored; the server chooses the provider (ROSC-144). */
    providerId: transcriptionRetrySchema.shape.providerId,
  })
  .refine(consistentTemplate, "Custom template selection is inconsistent")
  .refine(templateOptions, templateOptionsMessage);
const visitAudioCreateSchema = visitAudioSettingsSchema.safeExtend({
  audioUploadId: z.uuid().toLowerCase(),
});
export const visitCreateSchema = z.union([
  visitTranscriptCreateSchema,
  visitAudioCreateSchema,
]);
export type TranscriptVisitCreateInput = z.input<
  typeof visitTranscriptCreateSchema
>;
export type TranscriptVisitCreateBody = z.output<
  typeof visitTranscriptCreateSchema
>;
export type VisitCreateInput = z.input<typeof visitCreateSchema>;
export type VisitCreateBody = z.output<typeof visitCreateSchema>;
export const visitResultSchema = z.object({ public_id: publicIdSchema });

const decimalId = z
  .string()
  .regex(/^[1-9][0-9]*$/)
  .transform(Number)
  .pipe(z.int().positive());
export const visitListQuerySchema = z.strictObject({
  limit: decimalId.pipe(z.int().max(100)).default(50),
  cursor: publicIdSchema.optional(),
  // Opt-in, so clients that validate list items strictly (installed Chrome
  // extensions, older web builds) never receive `needsReviewCount`.
  include: z.literal("needsReview").optional(),
});
export const visitSummarySchema = z.object({
  public_id: publicIdSchema,
  rowNumber: z.int().nullable(),
  durationSeconds: z.number().nonnegative().nullable(),
  status: z.string().nullable(),
  title: z.string().nullable(),
  style: z.string().nullable(),
  format: z.string().nullable(),
  exam: z.string().nullable(),
  template: z.string().nullable(),
  macroPublicIds: z.array(publicIdSchema).nullable(),
  createdAt: z.string().nullable(),
  customTemplatePublicId: publicIdSchema.nullable(),
  // With `include=needsReview`: how many "[verify]" markers the visit's note
  // still has (values the clinician has not confirmed). Never the note text.
  needsReviewCount: z.int().nonnegative().optional(),
});
export const visitListSchema = z.object({
  items: z.array(visitSummarySchema),
  nextCursor: publicIdSchema.nullable(),
});
export const visitDetailSchema = visitSummarySchema.extend({
  name: z.string().nullable(),
  awsBucket: z.string().nullable(),
  transcription: z.string().nullable(),
  result: z.string().nullable(),
  original: z.string().nullable(),
  customTemplateName: z.string().nullable(),
  versions: z
    .array(
      z.object({
        public_id: publicIdSchema,
        createdAt: z.iso.datetime(),
        version: z.int(),
      }),
    )
    .max(5),
});
export type VisitListQuery = z.output<typeof visitListQuerySchema>;

const visitReadContract = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  NOT_FOUND: { message: "Not Found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});

export const visitContract = {
  listVisits: visitReadContract
    .input(
      z.object({
        params: z.strictObject({}).default({}),
        query: visitListQuerySchema.default({ limit: 50 }),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/visits",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitListSchema),
  getVisit: visitReadContract
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query: z.strictObject({}).default({}),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/visits/{public_id}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitDetailSchema),
  createVisit: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      PAYMENT_REQUIRED: { message: "Insufficient recording minutes" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Visit or template not found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      CONFLICT: { message: "Submission key was used with different input" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Visit creation unavailable" },
    })
    .input(
      z.object({
        params: z.strictObject({}).default({}),
        query: z.strictObject({}).default({}),
        headers: z.object({ "idempotency-key": z.uuid().toLowerCase() }),
        body: visitCreateSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/visits",
        inputStructure: "detailed",
        successStatus: 201,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitResultSchema),
};
